import {
  Flex,
  Heading,
  Icon,
  Popover,
  Spacer,
  Stack,
  Text,
} from '@backyard-ui/core';
import { InfoCircleOutline } from '@backyard-ui/icons';

import { type Pricing, type Price } from '@/infra/services/catalog/model';
import { type Pack } from '@/infra/services/catalog/product/get-product-info/model';
import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { formatPrice } from '@/utils/price/formatPrice';

import { Installments } from './Installments';
import { OnDemand } from './OnDemand';
import { Packing } from './Packing';
import { styles } from './PriceTag.styles';
import { ShopResume } from './ShopResume';
import { Unavailable } from './Unavailable';
import { buildTextColors, getPriceSuffix, getPromotionalPrice } from './utils';

interface PriceTagProps
  extends Partial<Pick<Seller, 'shop' | 'isLMFreightActive'>> {
  /**
   * Add price to original value. Can be used for optionals in service page.
   */
  additionalPrice?: Price;
  pricing: Pricing;
  unit: string;
  isOutlet?: boolean;
  isSoldOut?: boolean;
  onDemand?: boolean;
  pack?: Pack;
}

function PriceTag(props: Readonly<PriceTagProps>) {
  const {
    pricing,
    unit,
    isOutlet = false,
    isSoldOut = false,
    onDemand = false,
    isLMFreightActive = false,
    shop,
    pack,
  } = props;

  const { discount, price, installment } = pricing;

  if (isSoldOut) {
    return onDemand ? <OnDemand /> : <Unavailable />;
  }

  const promotionalPrice = getPromotionalPrice(price);

  // !discount.featureInstallmentDiscount avoids "two prices" from being treated as offer
  const isOffer =
    discount.percentage > 0 && !discount.featureInstallmentDiscount;

  const hasInstallment = installment.quantity > 1;

  const shouldShowInstallmentPrice = installment.totalTermPrice > price.to;
  const priceSuffix = getPriceSuffix(unit);
  const classNames = styles({ isOffer, isOutlet });

  const textColors = buildTextColors({
    isOffer,
    isOutlet,
  });

  return (
    <Stack className={classNames.container()} direction="column" spacing="4">
      <Flex direction="column">
        {isOffer && (
          <Flex justify="space-between" className={classNames.strip()}>
            <Text size="md" color={textColors.stripTitle} weight="bold" asChild>
              <span>{isOutlet ? 'BLACK FRIDAY' : 'OFERTA'}</span>
            </Text>
            <Heading size="xs" color={textColors.percentage} asChild>
              <span>{`-${discount.percentage}%`}</span>
            </Heading>
          </Flex>
        )}
        <Flex direction="column" className={classNames.header()}>
          {isOffer && (
            <>
              <Spacer size="1" axis="vertical" />
              <Text
                size="md"
                color={textColors.promotionalPrice}
                data-testid="promotional-price"
                asChild
              >
                <span className={classNames.promotionalPrice()}>
                  {String(formatPrice(promotionalPrice))}
                </span>
              </Text>
            </>
          )}
          <Spacer size="1.5" axis="vertical" />
          <Flex align="flex-end">
            <Heading size="xl" color={textColors.toPrice} asChild>
              <span>{formatPrice(price.to)}</span>
            </Heading>
            {priceSuffix && (
              <Heading size="md" color={textColors.priceSuffix} asChild>
                <span>{priceSuffix}</span>
              </Heading>
            )}
            {discount.paymentMethods && (
              <>
                <Spacer size="1" />
                <Text
                  size="md"
                  weight="bold"
                  color={textColors.priceLabel}
                  asChild
                >
                  <span>{discount.paymentMethods}</span>
                </Text>
              </>
            )}
          </Flex>
          <Spacer size="1.5" axis="vertical" />
          {shouldShowInstallmentPrice && (
            <>
              <Spacer size="1" axis="vertical" />
              <Flex align="flex-end">
                <Text
                  size="lg"
                  weight="bold"
                  color={textColors.dfpPrice}
                  asChild
                >
                  <span>{formatPrice(installment.totalTermPrice)}</span>
                </Text>
                {priceSuffix && (
                  <Text
                    size="sm"
                    weight="bold"
                    color={textColors.dfpPrice}
                    asChild
                  >
                    <span>{priceSuffix}</span>
                  </Text>
                )}
                <Spacer size="1" />
                <Text
                  size="md"
                  weight="bold"
                  color={textColors.priceLabel}
                  asChild
                >
                  <span>a prazo</span>
                </Text>
                {!hasInstallment && (
                  <>
                    <Spacer size="1" />
                    <Popover.Root>
                      <Popover.Trigger asChild>
                        <button className="pb-px leading-none text-neutral-700">
                          <Icon size="xs" color={textColors.infoButton}>
                            <InfoCircleOutline />
                          </Icon>
                        </button>
                      </Popover.Trigger>
                      <Popover.Content>
                        <Popover.Arrow />
                        <Popover.Body>
                          <Flex direction="column" asChild>
                            <p>
                              <span>Parcelamento somente em </span>
                              <span>
                                compras acima de <strong>R$200</strong>
                              </span>
                            </p>
                          </Flex>
                        </Popover.Body>
                      </Popover.Content>
                    </Popover.Root>
                  </>
                )}
              </Flex>
            </>
          )}
          <Installments
            installment={installment}
            textColor={textColors.installments}
          />
        </Flex>
      </Flex>
      <Packing pack={pack} />
      {shop && <ShopResume shop={shop} isLMFreightActive={isLMFreightActive} />}
    </Stack>
  );
}

export default PriceTag;
